// Team Cards System Constants
// Replaces Pools & Voting with performance-driven teams

// ========== TEAM TYPES ==========
export const TEAM_TYPES = {
  RECOVERY: 'recovery',    // Phase 1: Admin-created rugged project teams
  THEMATIC: 'thematic',    // Phase 2: Admin-curated community identity teams
};

export const TEAM_TYPE_LABELS = {
  [TEAM_TYPES.RECOVERY]: { 
    label: 'Recovery Team', 
    description: 'For rugged project survivors',
    icon: '🧨',
    color: 'orange'
  },
  [TEAM_TYPES.THEMATIC]: { 
    label: 'Community Team', 
    description: 'Cross-project collaboration',
    icon: '⚔️',
    color: 'purple'
  },
};

// ========== TEAM CONFIGURATION ==========
export const TEAM_CONFIG = {
  maxMembers: 3000,           // Variable up to 3000 per team
  minMembers: 1,              // Minimum to be active
  adminCreatedOnly: true,     // Phase 1 & 2: Admin-created only
  scoreUpdateInterval: 3600,  // Recalculate scores every hour (seconds)
};

// ========== TEAM SUCCESS SCORE FORMULA ==========
// Team Score = (Total XP × 0.4) + (Total BURNZ Burned × 0.4) + (Active Members × 0.2)
export const TEAM_SCORE_WEIGHTS = {
  totalXP: 0.4,
  totalBurnzBurned: 0.4,
  activeMembers: 0.2,  // Members active in last 7 days
};

// Calculate team success score
export const calculateTeamScore = (teamStats) => {
  const { totalXP = 0, totalBurnzBurned = 0, activeMembers = 0 } = teamStats;
  
  // Normalize values to prevent extreme scores
  const normalizedXP = Math.log10(Math.max(totalXP, 1)) * 1000;
  const normalizedBurns = Math.log10(Math.max(totalBurnzBurned, 1)) * 1000;
  const normalizedActive = activeMembers * 10;
  
  return Math.round(
    (normalizedXP * TEAM_SCORE_WEIGHTS.totalXP) +
    (normalizedBurns * TEAM_SCORE_WEIGHTS.totalBurnzBurned) +
    (normalizedActive * TEAM_SCORE_WEIGHTS.activeMembers)
  );
};

// ========== TEAM TIERS (Based on Success Score) ==========
export const TEAM_TIERS = [
  { 
    id: 'starter', 
    name: 'Starter', 
    minScore: 0, 
    icon: '🌱', 
    color: 'gray',
    gradient: 'from-gray-500 to-gray-700',
    benefits: ['Basic team badge', 'Team leaderboard visibility']
  },
  { 
    id: 'rising', 
    name: 'Rising', 
    minScore: 1000, 
    icon: '🔥', 
    color: 'orange',
    gradient: 'from-orange-500 to-orange-700',
    benefits: ['+2% XP bonus for members', 'Team profile customization', 'Team chat access']
  },
  { 
    id: 'established', 
    name: 'Established', 
    minScore: 5000, 
    icon: '⚡', 
    color: 'yellow',
    gradient: 'from-yellow-500 to-amber-600',
    benefits: ['+5% XP bonus', '-5% burn fee reduction', 'Featured on homepage rotation']
  },
  { 
    id: 'elite', 
    name: 'Elite', 
    minScore: 15000, 
    icon: '💎', 
    color: 'cyan',
    gradient: 'from-cyan-400 to-blue-600',
    benefits: ['+10% XP bonus', '-10% burn fee reduction', 'Team-exclusive quests', 'Priority support']
  },
  { 
    id: 'legendary', 
    name: 'Legendary', 
    minScore: 50000, 
    icon: '👑', 
    color: 'purple',
    gradient: 'from-purple-500 to-pink-600',
    benefits: ['+15% XP bonus', '-15% burn fee reduction', '+5% better conversion rate', 'Exclusive badge', 'Partner campaign priority']
  },
];

export const getTeamTier = (score) => {
  for (let i = TEAM_TIERS.length - 1; i >= 0; i--) {
    if (score >= TEAM_TIERS[i].minScore) {
      return TEAM_TIERS[i];
    }
  }
  return TEAM_TIERS[0];
};

// ========== TEAM REWARDS (NO RAW TOKENS) ==========
// Teams get prestige & efficiency, NOT direct BURNZ
export const TEAM_REWARDS = {
  // Seasonal rewards (non-stacking)
  seasonalTop1: {
    badge: 'Season Champion Team',
    xpBonus: 0.10,         // +10% XP for 30 days
    conversionBonus: 0.05,  // +5% better conversion rate
    burnFeeReduction: 0.10, // -10% burn fees for 14 days
    featuredDays: 30,
    burnz: 0,  // NO direct BURNZ
  },
  seasonalTop3: {
    badge: 'Season Elite Team',
    xpBonus: 0.05,
    conversionBonus: 0.03,
    burnFeeReduction: 0.05,
    featuredDays: 14,
    burnz: 0,
  },
  seasonalTop10: {
    badge: 'Season Contender',
    xpBonus: 0.03,
    conversionBonus: 0,
    burnFeeReduction: 0.03,
    featuredDays: 7,
    burnz: 0,
  },
  
  // Weekly milestone rewards
  weeklyBurnLeader: {
    badge: 'Top Burner Team',
    burnFeeReduction: 0.05,
    featuredDays: 7,
    burnz: 0,
  },
  weeklyXPLeader: {
    badge: 'Top Grinder Team',
    xpBonus: 0.05,
    featuredDays: 7,
    burnz: 0,
  },
  weeklyGrowthLeader: {
    badge: 'Fastest Growing',
    featuredDays: 7,
    burnz: 0,
  },
};

// ========== TEAM BADGES ==========
export const TEAM_BADGES = [
  // Achievement badges
  { id: 'team_first_100', name: 'Century Club', icon: '💯', description: 'Reach 100 members', rarity: 'common' },
  { id: 'team_first_500', name: 'Battalion', icon: '🏰', description: 'Reach 500 members', rarity: 'rare' },
  { id: 'team_first_1000', name: 'Legion', icon: '⚔️', description: 'Reach 1000 members', rarity: 'epic' },
  { id: 'team_first_3000', name: 'Army', icon: '🏛️', description: 'Reach 3000 members', rarity: 'legendary' },
  
  // Burn badges
  { id: 'team_burn_10k', name: 'Kindling', icon: '🔥', description: 'Team burns 10K BURNZ', rarity: 'common' },
  { id: 'team_burn_100k', name: 'Bonfire', icon: '🔥', description: 'Team burns 100K BURNZ', rarity: 'rare' },
  { id: 'team_burn_1m', name: 'Inferno', icon: '🔥', description: 'Team burns 1M BURNZ', rarity: 'epic' },
  { id: 'team_burn_10m', name: 'Supernova', icon: '☄️', description: 'Team burns 10M BURNZ', rarity: 'legendary' },
  
  // XP badges
  { id: 'team_xp_100k', name: 'Grinders', icon: '⚡', description: 'Team earns 100K XP', rarity: 'common' },
  { id: 'team_xp_1m', name: 'Powerhouse', icon: '⚡', description: 'Team earns 1M XP', rarity: 'rare' },
  { id: 'team_xp_10m', name: 'Unstoppable', icon: '💪', description: 'Team earns 10M XP', rarity: 'epic' },
  
  // Seasonal badges
  { id: 'season_champion', name: 'Season Champion', icon: '🏆', description: 'Win a season', rarity: 'mythic' },
  { id: 'season_top3', name: 'Season Podium', icon: '🥇', description: 'Top 3 in season', rarity: 'legendary' },
  { id: 'top_burner_week', name: 'Weekly Flame', icon: '🔥', description: 'Top burning team of the week', rarity: 'rare' },
];

// ========== DEMO TEAMS (Phase 1: Recovery) ==========
export const DEMO_TEAMS = [
  {
    id: 'team_dead_apes',
    name: 'Dead Apes Recovery',
    type: TEAM_TYPES.RECOVERY,
    icon: '🧨',
    description: 'For survivors of the Neon Apes Genesis rug. Rebuild together.',
    originalProject: 'Neon Apes Genesis',
    policyId: 'policy123...',
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400',
    memberCount: 847,
    activeMembers: 423,
    totalXP: 2847320,
    totalBurnzBurned: 145000,
    score: 0, // Will be calculated
    tier: 'elite',
    badges: ['team_first_500', 'team_burn_100k', 'team_xp_1m'],
    weeklyXP: 125000,
    weeklyBurns: 12500,
    createdAt: '2025-12-01',
    rank: 1,
  },
  {
    id: 'team_rugged_lions',
    name: 'Rugged Lions',
    type: TEAM_TYPES.RECOVERY,
    icon: '⚰️',
    description: 'Cyber Punks CNF community seeking collective closure and new opportunities.',
    originalProject: 'Cyber Punks CNF',
    policyId: 'policy456...',
    image: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=400',
    memberCount: 634,
    activeMembers: 289,
    totalXP: 1923450,
    totalBurnzBurned: 98000,
    score: 0,
    tier: 'established',
    badges: ['team_first_500', 'team_burn_10k', 'team_xp_100k'],
    weeklyXP: 87000,
    weeklyBurns: 8700,
    createdAt: '2025-12-05',
    rank: 2,
  },
  {
    id: 'team_abandoned_lands',
    name: 'Abandoned Lands',
    type: TEAM_TYPES.RECOVERY,
    icon: '🔥',
    description: 'Meta Monkeys DAO survivors working towards ecosystem cleanup.',
    originalProject: 'Meta Monkeys DAO',
    policyId: 'policy789...',
    image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
    memberCount: 412,
    activeMembers: 198,
    totalXP: 987650,
    totalBurnzBurned: 45000,
    score: 0,
    tier: 'rising',
    badges: ['team_first_100', 'team_burn_10k', 'team_xp_100k'],
    weeklyXP: 54000,
    weeklyBurns: 5400,
    createdAt: '2025-12-10',
    rank: 3,
  },
  {
    id: 'team_ghost_mint',
    name: 'Ghost Mint Survivors',
    type: TEAM_TYPES.RECOVERY,
    icon: '🪦',
    description: 'United survivors from projects that vanished after mint.',
    originalProject: 'Various Ghost Projects',
    policyId: null,
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400',
    memberCount: 256,
    activeMembers: 134,
    totalXP: 543210,
    totalBurnzBurned: 28000,
    score: 0,
    tier: 'rising',
    badges: ['team_first_100', 'team_burn_10k'],
    weeklyXP: 32000,
    weeklyBurns: 3200,
    createdAt: '2025-12-15',
    rank: 4,
  },
];

// Calculate scores for demo teams
DEMO_TEAMS.forEach(team => {
  team.score = calculateTeamScore({
    totalXP: team.totalXP,
    totalBurnzBurned: team.totalBurnzBurned,
    activeMembers: team.activeMembers,
  });
  team.tier = getTeamTier(team.score).id;
});

// ========== DEMO TEAMS (Phase 2: Thematic) ==========
export const DEMO_THEMATIC_TEAMS = [
  {
    id: 'team_burners_guild',
    name: 'Burners Guild',
    type: TEAM_TYPES.THEMATIC,
    icon: '🔥',
    description: 'For those who burn with purpose. Maximum deflation, maximum impact.',
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=400',
    memberCount: 1247,
    activeMembers: 678,
    totalXP: 5847320,
    totalBurnzBurned: 892000,
    score: 0,
    tier: 'legendary',
    badges: ['team_first_1000', 'team_burn_100k', 'team_xp_1m', 'season_top3'],
    weeklyXP: 245000,
    weeklyBurns: 45000,
    createdAt: '2026-01-01',
    rank: 1,
  },
  {
    id: 'team_rebuilders',
    name: 'The Rebuilders',
    type: TEAM_TYPES.THEMATIC,
    icon: '⚔️',
    description: 'From ashes to excellence. We rebuild what was broken.',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400',
    memberCount: 892,
    activeMembers: 456,
    totalXP: 3234560,
    totalBurnzBurned: 234000,
    score: 0,
    tier: 'elite',
    badges: ['team_first_500', 'team_burn_100k', 'team_xp_1m'],
    weeklyXP: 156000,
    weeklyBurns: 15600,
    createdAt: '2026-01-05',
    rank: 2,
  },
  {
    id: 'team_xp_maxers',
    name: 'XP Maxers',
    type: TEAM_TYPES.THEMATIC,
    icon: '🧠',
    description: 'Grind smarter, not harder. XP optimization specialists.',
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400',
    memberCount: 567,
    activeMembers: 312,
    totalXP: 4567890,
    totalBurnzBurned: 123000,
    score: 0,
    tier: 'elite',
    badges: ['team_first_500', 'team_xp_1m', 'top_burner_week'],
    weeklyXP: 289000,
    weeklyBurns: 8900,
    createdAt: '2026-01-08',
    rank: 3,
  },
  {
    id: 'team_diamond_hands',
    name: 'Diamond Hands Union',
    type: TEAM_TYPES.THEMATIC,
    icon: '💎',
    description: 'We held through the worst. Now we rise together.',
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400',
    memberCount: 734,
    activeMembers: 398,
    totalXP: 2876540,
    totalBurnzBurned: 198000,
    score: 0,
    tier: 'elite',
    badges: ['team_first_500', 'team_burn_100k', 'team_xp_1m'],
    weeklyXP: 134000,
    weeklyBurns: 13400,
    createdAt: '2026-01-10',
    rank: 4,
  },
];

// Calculate scores for thematic teams
DEMO_THEMATIC_TEAMS.forEach(team => {
  team.score = calculateTeamScore({
    totalXP: team.totalXP,
    totalBurnzBurned: team.totalBurnzBurned,
    activeMembers: team.activeMembers,
  });
  team.tier = getTeamTier(team.score).id;
});

// ========== TEAM LEADERBOARD ==========
export const TEAM_LEADERBOARD_TYPES = {
  OVERALL: 'overall',      // By team score
  XP: 'xp',                // By total XP
  BURNS: 'burns',          // By total burns
  GROWTH: 'growth',        // By member growth rate
  WEEKLY: 'weekly',        // By weekly performance
};

// ========== MEMBER CONTRIBUTION DISPLAY ==========
export const getMemberContributionPercent = (memberBurns, teamTotalBurns) => {
  if (!teamTotalBurns || teamTotalBurns === 0) return 0;
  return ((memberBurns / teamTotalBurns) * 100).toFixed(2);
};

// UI copy for burn visibility
export const TEAM_BURN_MESSAGES = {
  personal: (amount) => `You burned ${amount} $BURNZ today.`,
  teamContribution: (percent) => `That's ${percent}% of your team's weekly burn.`,
  teamTotal: (amount) => `Team total: ${amount.toLocaleString()} $BURNZ burned`,
  impact: (teamRank) => `Your team is #${teamRank} in burns this week!`,
};

// ========== MIGRATION FROM POOLS ==========
// Map old pool concepts to new team concepts
export const POOL_TO_TEAM_MIGRATION = {
  // Old pool concept → New team concept
  'pool' : 'team',
  'pool_member': 'team_member',
  'pool_milestone': 'team_achievement',
  'pool_voting': null,  // REMOVED
  'shared_rewards': 'team_advantages',
  'governance': null,   // REMOVED
};
